import { MARKS } from '@contentful/rich-text-types';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useMediaQuery } from 'react-responsive';

import CTALinks from 'components/CTALinks';
import Container from 'components/Grid/Container';
import { PrimaryHeading } from 'components/Headings';
import Image from 'components/ImageV2';
import Pagination from 'components/Pagination';
import Paragraph from 'components/Paragraph';
import RichText from 'components/RichText';
import { PAGE_SECTIONS } from 'constants/contentHubPage';
import { mediumUp } from 'constants/mediaQueries';
import ContentHubPageModel from 'models/ContentHubPage';
import Theme from 'styles/theme';
import I18n from 'utils/I18n';
import { parseQueryString, replaceHistory } from 'utils/Url';

import ArticleCard from './ArticleCard';

const renderPageSection = data => {
  const options = {
    renderMark: {
      [MARKS.UNDERLINE]: text => <span>{text}</span>,
    },
  };
  if (!data) {
    return null;
  }

  switch (data._contentType) {
    case PAGE_SECTIONS.SUBHEADER: {
      return (
        <RichText
          content={data.copy}
          options={options}
          className="ContentHubPage__section-subheader"
        />
      );
    }
    case PAGE_SECTIONS.ARTICLE_PAGE: {
      return (
        <ArticleCard
          header={data.articleHeader}
          body={data.description}
          linkTo={`/pages/${data.handle}`}
          image={data.image}
          date={data.date}
        />
      );
    }

    default:
      return null;
  }
};

const ITEMS_PER_PAGE = 9;

const ContentHubPage = ({ contentHubPage }) => {
  const queryParams = parseQueryString();
  const [currentPage, setCurrentPage] = useState(Number(queryParams.page) || 1);
  const isMediumUp = useMediaQuery({ query: mediumUp });
  const {
    heroHeadline,
    heroSubheadline,
    heroBackgroundColor,
    heroTextColor,
    heroCtaItems,
    heroBackgroundImage,
    articles,
    subheader,
  } = contentHubPage;

  const currentArticles = articles.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const startOfRange = (currentPage - 1) * ITEMS_PER_PAGE + 1;
  const endOfRange = Math.min(currentPage * ITEMS_PER_PAGE, articles.length);
  const visibleRange = `${startOfRange} - ${endOfRange}`;
  const helperText = I18n.localize('contentHubPage.showingArticles', {
    visibleArticles: visibleRange,
    totalArticles: articles.length,
  });
  const totalPages = Math.ceil(articles.length / ITEMS_PER_PAGE);

  // Update the URL when the page changes
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);

    replaceHistory({ ...queryParams, page: currentPage });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage, totalPages]);

  const handlePaginationClick = page => {
    setCurrentPage(page);
  };

  const responsiveBackgroundImage = isMediumUp
    ? heroBackgroundImage?.landscape
    : heroBackgroundImage?.portrait;

  return (
    <div className="ContentHubPage">
      <div className="ContentHubPage__hero">
        {responsiveBackgroundImage && (
          <div className="ContentHubPage__background-wrapper">
            <Image
              src={responsiveBackgroundImage.src}
              width={responsiveBackgroundImage.width}
              height={responsiveBackgroundImage.height}
              alt=""
              className="ContentHubPage__background-image"
              role="presentation"
              coverParentNode
            />
          </div>
        )}
        <div className="ContentHubPage__hero-content">
          <div className="ContentHubPage__hero-body">
            <PrimaryHeading className="ContentHubPage__hero-heading">{heroHeadline}</PrimaryHeading>
            {heroSubheadline && <Paragraph>{heroSubheadline}</Paragraph>}
          </div>

          {heroCtaItems.length > 0 && <CTALinks links={heroCtaItems} />}
        </div>
      </div>

      <Container className="ContentHubPage__sections">
        {subheader && (
          <div className="ContentHubPage__subheader">
            {renderPageSection(subheader)}
            <span className="ContentHubPage__pagination-helper">{helperText}</span>
          </div>
        )}
        <ul className="ContentHubPage__article-list">
          {currentArticles.length > 0 &&
            currentArticles.map(article => {
              return (
                <li
                  className={classNames('ContentHubPage__article-item')}
                  key={`content-hub-section-${article.internalName}`}
                >
                  {renderPageSection(article)}
                </li>
              );
            })}
        </ul>
      </Container>
      {totalPages > 1 && (
        <Container>
          <Pagination
            onClick={handlePaginationClick}
            totalPages={totalPages}
            currentPage={currentPage}
          />
        </Container>
      )}
      <style jsx>{`
        .ContentHubPage {
          padding-bottom: ${Theme.get('spacing') * 6}px;
        }

        .ContentHubPage__hero {
          color: ${heroTextColor};
          position: relative;
          background-color: ${heroBackgroundColor};
        }

        .ContentHubPage__hero-body {
          width: 75%;
          margin: 0 auto ${Theme.get('spacing') * 2}px;
          text-align: center;
        }

        .ContentHubPage__pagination-helper {
          display: block;
          font-family: ${Theme.get('font-family')};
          font-size: ${Theme.get('font-size-description-px')};
          color: ${Theme.get('colors-black')};
        }

        .ContentHubPage__hero-content {
          position: relative;
          display: flex;
          flex-direction: column;
          padding: ${Theme.get('spacing') * 4}px;
          padding-top: ${Theme.get('spacing') * 12}px;
          min-height: 281px;
          height: 35vh;
          max-width: 1104px;
        }

        .ContentHubPage__background-wrapper {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
        }

        .ContentHubPage__article-list {
          display: grid;
          gap: ${Theme.get('spacing') * 8}px;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
        }

        .ContentHubPage__subheader {
          display: flex;
          flex-direction: column;
          margin-bottom: ${Theme.get('spacing') * 6}px;
        }

        @media ${mediumUp} {
          .ContentHubPage__hero-content {
            flex-direction: row;
            padding: ${Theme.get('spacing') * 4}px;
            align-items: center;
            justify-content: space-between;
            margin: auto;
            min-height: 329px;
            height: 35vh;
          }

          .ContentHubPage__pagination-helper {
            line-height: 1.5;
          }

          .ContentHubPage__hero-body {
            text-align: left;
            padding: 0;
            margin: 0;
            width: auto;
          }

          .ContentHubPage__article-list {
            gap: ${Theme.get('spacing') * 9}px;
          }

          .ContentHubPage__article-item {
            max-width: 407px;
          }

          .ContentHubPage__subheader {
            flex-direction: row;
            justify-content: space-between;
            align-items: end;
            margin-bottom: ${Theme.get('spacing') * 5}px;
          }
        }
      `}</style>

      <style jsx global>{`
        .ContentHubPage__hero .typography--primary-heading,
        .ContentHubPage__hero .Paragraph {
          color: currentColor;
          margin: 0;
        }

        .ContentHubPage__hero .ContentHubPage__hero-heading {
          margin-bottom: ${Theme.get('spacing') * 2}px;
        }

        .ContentHubPage__hero .CTALinks {
          margin-top: auto;
          width: 100%;
          justify-content: space-between;
          gap: ${Theme.get('spacing') * 3}px;
        }

        .ContentHubPage__hero .CTALinks--single {
          justify-content: center;
        }

        .ContentHubPage__hero .CTALinks .Button {
          margin: 0;
          width: 50%;
          max-width: 196px;
          border: none;
          background-color: ${Theme.get('colors-white')};
          color: ${Theme.get('colors-black')};
        }

        .ContentHubPage__hero .CTALinks .Button:hover {
          background-color: ${Theme.get('colors-black')};
          color: ${Theme.get('colors-white')};
        }

        .Container.ContentHubPage__sections {
          margin-bottom: ${Theme.get('spacing') * 6}px;
        }

        .ContentHubPage__section-subheader.RichText {
          padding-top: ${Theme.get('spacing') * 6}px;
        }

        .ContentHubPage__section-subheader.RichText .Paragraph {
          font-size: ${Theme.get('font-size-description-px')};
        }

        .ContentHubPage__section-subheader.RichText .typography--secondary-heading {
          margin-bottom: ${Theme.get('spacing') * 3}px;
          padding: 0;
        }

        .ContentHubPage__section-subheader.RichText .Paragraph {
          margin: 0;
        }

        .ContentHubPage__section-subheader {
          margin-bottom: ${Theme.get('spacing') * 2}px;
        }

        @media ${mediumUp} {
          .ContentHubPage__hero .CTALinks {
            margin: 0;
            flex-direction: column;
            width: 18%;
            min-width: 190px;
          }

          .ContentHubPage__hero .CTALinks .Button {
            width: auto;
          }

          .ContentHubPage__section-subheader {
            margin-bottom: 0;
          }
        }
      `}</style>
    </div>
  );
};
ContentHubPage.propTypes = {
  contentHubPage: PropTypes.instanceOf(ContentHubPageModel).isRequired,
};

export default ContentHubPage;
